import { useState, useCallback, useRef } from "react";
import { SSEClient, SSEOptions } from "@/app/utils/sse";
import {
  ReportBizTypeEnum,
  ReportKeywordContentEnum,
  ReportParentContentEnum,
  ReportSceneTypeEnum,
} from "@/app/types/typeConfig/report";
import { OriginType } from "@/app/types/typeConfig/chatQuestion";
import useReportRequestQueue from "./useReportRequestQueue";
import request from "@/request";
import { isApp } from "@/app/bridge";
import { createClientUUid, createUuid } from "@/app/utils/string";
import {
  MessageType,
  MessageSender,
  MessageStatus,
  HeaderType,
  RobotMessageType,
  RobotMessage,
} from "@/app/store/types";
import { useMessageStore } from "@/app/store";
import { closeOpenAIChat } from "@/app/api/robot/chatContent";

export interface AIMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: number;
  streaming?: boolean;
}

export interface UseAIChatOptions {
  /**
   * SSE 端点 URL
   */
  url: string;
  /**
   * 发送消息的 API 端点
   */
  sendUrl: string;
  /**
   * 每次问答的超时时间
   * @default 60000
   */
  timeout?: number;
  /**
   * 是否自动重连（针对网络错误）
   * @default false
   */
  autoReconnect?: boolean;
  /**
   * 调试模式
   * @default false
   */
  debug?: boolean;
  /**
   * 自定义请求头
   */
  headers?: Record<string, string>;

  sessionId?: string;

  /**
   * 流式回复结束时的回调（正常结束 / 服务端或网络错误 / 用户停止）
   */
  onStreamEnd?: (payload: {
    reason: "done" | "error" | "stop";
    messageId: string;
  }) => void;
}

export interface UseAIChatReturn {
  /**
   * 对话历史
   */
  messages: AIMessage[];
  /**
   * 当前正在流式输出的消息内容
   */
  streamingContent: string;
  /**
   * 是否正在进行中
   */
  isStreaming: boolean;
  /**
   * 错误信息
   */
  error: Error | null;
  /**
   * 发送消息（开始新的 AI 问答）
   */
  sendMessage: (
    params: StartConnectionPropsType,
    connectSuccessCallback?: () => void,
    isRetry?: boolean,
    handyHmsOnly?: boolean,
  ) => Promise<void>;
  /**
   * 停止当前问答
   */
  stopStreaming: () => void;
  /**
   * 清空对话历史
   */
  clearMessages: () => void;
  /**
   * 重新发送最后一条消息
   */
  retry: () => Promise<void>;
}

export interface StartConnectionPropsType {
  bizType: ReportBizTypeEnum;
  content: string;
  contentId?: string;
  contentType: ReportKeywordContentEnum;
  parentContent?: string;
  parentContentId?: string;
  parentContentType?: ReportParentContentEnum;
  requestId?: string;
}

/**
 * AI 聊天 Hook - 一次性连接模式
 *
 * 特点：
 * 1. 每次问答建立一次 SSE 连接
 * 2. 问答结束后自动断开连接
 * 3. 在问答进行中时不能发起新问答
 * 4. 支持流式显示 AI 回复
 *
 * @example
 * ```tsx
 * const { messages, streamingContent, isStreaming, sendMessage } = useAIChat({
 *   url: '/api/ai/chat'
 * });
 *
 * // 发送问题
 * await sendMessage('你好');
 *
 * // 显示消息
 * {messages.map(msg => <div>{msg.content}</div>)}
 * {isStreaming && <div>{streamingContent}</div>}
 * ```
 */
export function useAIChat(options: UseAIChatOptions): UseAIChatReturn {
  const {
    url,
    sendUrl,
    timeout = 60000,
    autoReconnect = false,
    debug = false,
    headers,
    sessionId,
    onStreamEnd,
  } = options;

  const {
    setRobotMessage,
    addOutgoingMessage,
    updateRobotMessage,
    setAiTypingEnd,
    setGlobalStopStreaming,
    setIsAIStreaming,
  } = useMessageStore();
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const sseClientRef = useRef<SSEClient | null>(null);
  const lastUserMessageRef = useRef<string>("");
  const lastRequestDataRef = useRef<StartConnectionPropsType | null>(null);
  const streamingMessageIdRef = useRef<string>("");
  const streamingContentRef = useRef<string>("");
  const currentRequestIdRef = useRef<string>("");

  const errorRef = useRef(error);
  errorRef.current = error;
  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;
  const stopStreamingRef = useRef<() => void>(() => {});

  const { requireReportProps } = useReportRequestQueue();

  const log = useCallback(
    (message: string, ...args: unknown[]) => {
      if (debug) {
        console.log(`[useAIChat] ${message}`, ...args);
      }
    },
    [debug],
  );

  /**
   * 停止当前流
   */
  const stopStreaming = useCallback(() => {
    const messageId = streamingMessageIdRef.current;
    if (messageId) {
      onStreamEnd?.({ reason: "stop", messageId });
    }
    // 如果当前消息没有错误，说明是手动停止
    if (!errorRef.current && messageId) {
      updateRobotMessage(streamingMessageIdRef.current, {
        streaming: false,
        error: true,
      });
    }
    log("停止当前问答");
    if (currentRequestIdRef.current && sessionId) {
      closeOpenAIChat(currentRequestIdRef.current, sessionId);
    }
    if (sseClientRef.current) {
      log("关闭 SSE 连接");
      sseClientRef.current.close();
      sseClientRef.current = null;
    }
    setIsStreaming(false);
    setIsAIStreaming(false);
    setStreamingContent("");
    setAiTypingEnd(true);
    setGlobalStopStreaming(null);
  }, [
    log,
    sessionId,
    updateRobotMessage,
    setIsAIStreaming,
    setAiTypingEnd,
    setGlobalStopStreaming,
  ]);

  stopStreamingRef.current = stopStreaming;

  /**
   * 发送消息
   */
  const sendMessage = useCallback(
    async (
      params: StartConnectionPropsType,
      connectSuccessCallback?: () => void,
      isRetry: boolean = false,
      handyHmsOnly?: boolean,
    ) => {
      if (isStreamingRef.current) {
        const errorMsg = "当前问答进行中，请等待完成后再发送";
        log(errorMsg);
        throw new Error(errorMsg);
      }

      if (!params.content.trim()) {
        throw new Error("消息内容不能为空");
      }

      log(isRetry ? "重试消息:" : "发送消息:", params.content);

      // 保存最后的消息，用于重试
      lastUserMessageRef.current = params.content;
      lastRequestDataRef.current = params;

      // 重置状态
      setError(null);
      setStreamingContent("");
      setIsStreaming(true);
      setIsAIStreaming(true);
      setAiTypingEnd(false); // 开始新的流式响应，重置打字状态

      const requestData = {
        message: params.content.trim(),
        timestamp: Date.now(),
      };

      // 创建 SSE 连接
      const sseOptions: SSEOptions = {
        url,
        method: "GET",
        data: requestData,
        headers: {
          ...headers,
          "x-robot-user": sessionId || "",
        },
        timeout,
        autoReconnect, // 只针对网络错误重连，不是问答重连
        maxReconnectAttempts: autoReconnect ? 2 : 0,
        reconnectInterval: 1000,
      };
      let requestId = "";
      let currentMessageId = "";
      // 用于标记是否已调用过 connectSuccessCallback
      let hasCalledSuccessCallback = false;
      // 用于标记 chat 请求是否失败
      let chatRequestFailed = false;
      // 用于存储 chat 请求的错误
      let chatRequestError: Error | null = null;

      if (isRetry) {
        requestId = currentRequestIdRef.current;
        currentMessageId = streamingMessageIdRef.current;
      } else {
        requestId = createClientUUid(MessageType.Text);
        currentRequestIdRef.current = requestId; // 保存当前请求ID

        // 为每个请求创建独立的 messageId，避免被后续请求覆盖
        currentMessageId = `assistant-${Date.now()}-${requestId}`;
        streamingMessageIdRef.current = currentMessageId; // 更新 ref（用于 retry 等功能）
      }

      let currentStreamingContent = ""; // 每个请求独立的内容累积
      /** 本轮 SSE 内关联问接口只请求一次 */
      let suggestedQuestionsFetched = false;

      try {
        const client = new SSEClient(sseOptions, {
          onOpen: async () => {
            // 检查是否是最新的请求，防止旧请求的回调覆盖新请求
            if (currentRequestIdRef.current !== requestId) {
              log("检测到过时的请求，忽略此连接");
              client.close();
              return;
            }

            log(
              "SSE 连接已建立, requestId:",
              requestId,
              "messageId:",
              currentMessageId,
            );
            currentStreamingContent = ""; // 重置本次请求的内容
            // 发送聊天请求 - 使用 await 等待完成
            try {
              await sendChatRequest(params, requestId);
              // 只有非重试时才添加用户消息
              if (!isRetry) {
                const userMessage = {
                  headerType: HeaderType.MESSAGE,
                  clientId: createClientUUid(MessageType.Text),
                  content: `{"text":"${params.content}"}`,
                  senderType: MessageSender.USER,
                  type: MessageType.Text,
                  timestamp: Date.now(),
                  status: MessageStatus.Success,
                  handyHmsOnly: handyHmsOnly || false,
                };
                streamingMessageIdRef.current = currentMessageId; // 更新 ref（用于 retry 等功能）
                // 塞入用户信息
                addOutgoingMessage(userMessage);
                // 塞入AI消息占位
                setRobotMessage({
                  id: currentMessageId,
                  content: "",
                  timestamp: Date.now(),
                  streaming: true,
                  clientId: createUuid(),
                  type: MessageType.Text,
                  headerType: HeaderType.MESSAGE,
                  senderType: MessageSender.ROBOT,
                  messageType: RobotMessageType.ROBOT_SSE_REPLY,
                  senderName: "智能助手",
                  robotData: {
                    questions: "",
                    requestId: requestId,
                    stopStreaming: () => stopStreamingRef.current(),
                    retry: async () => {
                      retry();
                    },
                    parentContentInfo: {
                      parentContent: params.parentContent,
                      parentContentId: params.parentContentId,
                      parentContentType: params.parentContentType,
                    },
                  },
                });
              }
              // 不在这里调用 connectSuccessCallback
              // 将在 onMessage 收到第一条有效数据时调用
            } catch (err) {
              log("发送聊天请求失败:", err);
              chatRequestFailed = true;
              chatRequestError =
                err instanceof Error ? err : new Error(String(err));
              stopStreamingRef.current();
              setStreamingContent("");
              // chat 请求失败时，不调用 connectSuccessCallback
              // 不在这里抛出异常，而是存储错误，稍后在外层抛出
            }
          },

          onMessage: (data: string) => {
            // 检查是否是最新的请求，防止旧请求的数据更新新消息
            if (currentRequestIdRef.current !== requestId) {
              log(
                "检测到过时的请求数据，忽略, 当前requestId:",
                currentRequestIdRef.current,
                "消息requestId:",
                requestId,
              );
              return;
            }

            try {
              if (data === "[DONE]") {
                // 特殊结束标志
                log(
                  "收到结束标志 [DONE]，结束问答, messageId:",
                  currentMessageId,
                );

                // 保存完整消息到历史
                // setMessages((prev) => {
                //   const assistantMessage: AIMessage = {
                //     id: currentMessageId,
                //     role: "assistant",
                //     content: currentStreamingContent,
                //     timestamp: Date.now(),
                //   };
                //   return [...prev, assistantMessage];
                // });

                updateRobotMessage(currentMessageId, {
                  content: currentStreamingContent,
                  streaming: false,
                });
                // 关闭连接；不在此处调用 setAiTypingEnd，等打字机渲染完成后再由 AiAnswerQuestion 的 onTypingEnd 触发
                setTimeout(() => {
                  if (sseClientRef.current) {
                    sseClientRef.current.close();
                    sseClientRef.current = null;
                  }
                  setIsStreaming(false);
                  setIsAIStreaming(false);
                  setError(null);
                  onStreamEnd?.({
                    reason: "done",
                    messageId: currentMessageId,
                  });
                  // setStreamingContent("");
                }, 200);
                return;
              }
              if (data === "[ERROR]" || data === "[error]") {
                updateRobotMessage(currentMessageId, {
                  content: currentStreamingContent,
                  streaming: false,
                });
                log("收到错误标志 [ERROR]，结束问答");
                setError(new Error("服务端错误"));
                onStreamEnd?.({ reason: "error", messageId: currentMessageId });
                stopStreamingRef.current();
                // 如果收到错误，不调用 connectSuccessCallback
                return;
              }

              // 尝试解析 JSON（可能与 delta 同级携带 messageId、aiFlag 等）
              const parsed = JSON.parse(data) as {
                delta?: string;
                messageId?: string | number;
                aiFlag?: boolean;
              };
              const {
                delta,
                messageId: sseMessageId,
                aiFlag: sseAiFlag,
              } = parsed;

              const mid =
                sseMessageId !== undefined && sseMessageId !== null
                  ? String(sseMessageId).trim()
                  : "";
              if (mid && !suggestedQuestionsFetched && sessionId) {
                suggestedQuestionsFetched = true;
                // 由于大模型暂时没有开放，先注释掉
                // getSuggestedQuestions(mid, sessionId)
                //   .then((res) => {
                //     if (currentRequestIdRef.current !== requestId) return;
                //     const msg = useMessageStore
                //       .getState()
                //       .msgList.find((m) => m.id === currentMessageId) as
                //       | RobotMessage
                //       | undefined;
                //     if (!msg) return;
                //     updateRobotMessage(currentMessageId, {
                //       robotData: {
                //         ...(msg.robotData ?? {}),
                //         sseMessageId: mid,
                //         suggestedQuestions: res.suggestedQuestions,
                //       },
                //     });
                //   })
                //   .catch((err) => {
                //     log("getSuggestedQuestions 失败:", err);
                //   });
              }

              const deltaText =
                typeof delta === "string"
                  ? delta
                  : delta !== undefined && delta !== null
                    ? String(delta)
                    : "";
              // 尝试解析 JSON
              // const parsed = JSON.parse(data);
              // const { delta, messageId } = parsed;

              // 在收到第一条有效数据时调用 connectSuccessCallback
              // 但要确保 chat 请求没有失败
              if (
                !hasCalledSuccessCallback &&
                connectSuccessCallback &&
                !chatRequestFailed &&
                (deltaText.length > 0 || mid.length > 0)
              ) {
                hasCalledSuccessCallback = true;
                connectSuccessCallback();
              }

              setStreamingContent((prev) => prev + deltaText);
              currentStreamingContent += deltaText; // 使用闭包变量累积内容
              // 这里可以处理 delta 的内容，比如更新 UI
              const prevMsg = useMessageStore
                .getState()
                .msgList.find((m) => m.id === currentMessageId) as
                | RobotMessage
                | undefined;
              const nextRobotData: RobotMessage["robotData"] = {
                ...(prevMsg?.robotData ?? {}),
              };
              if (sseAiFlag !== undefined) {
                nextRobotData.aiFlag = sseAiFlag;
              }
              updateRobotMessage(currentMessageId, {
                content: currentStreamingContent,
                streaming: true,
                streamingId: sseMessageId as string,
                robotData: nextRobotData,
              });
            } catch {
              // 不是 JSON，直接作为文本内容
              log("非 JSON 格式，作为文本处理:", data);

              // 在收到第一条有效数据时调用 connectSuccessCallback
              // 但要确保 chat 请求没有失败
              if (
                !hasCalledSuccessCallback &&
                connectSuccessCallback &&
                !chatRequestFailed
              ) {
                hasCalledSuccessCallback = true;
                connectSuccessCallback();
              }

              setStreamingContent((prev) => prev + data);
              currentStreamingContent += data;
              updateRobotMessage(currentMessageId, {
                content: currentStreamingContent,
                streaming: true,
              });
            }
          },

          onError: (err: Error) => {
            // 检查是否是最新的请求
            if (currentRequestIdRef.current !== requestId) {
              log("检测到过时的请求错误，忽略");
              return;
            }

            log("SSE 错误:", err, "messageId:", currentMessageId);
            setError(err);
            setIsStreaming(false);
            setIsAIStreaming(false);
            setAiTypingEnd(true); // 错误结束，同步打字结束状态

            // 停止打印效果并更新为错误状态（可重试）
            updateRobotMessage(currentMessageId, {
              content: currentStreamingContent || "服务超时，请重试",
              streaming: false,
              error: true, // 标记为错误状态
            });

            onStreamEnd?.({ reason: "error", messageId: currentMessageId });

            if (requestId && sessionId) {
              closeOpenAIChat(requestId, sessionId);
            }
            // 关闭连接
            if (sseClientRef.current) {
              sseClientRef.current.close();
              sseClientRef.current = null;
            }

            setStreamingContent("");
          },

          onClose: () => {
            if (currentRequestIdRef.current !== requestId) {
              log("检测到过时的请求关闭事件，忽略");
              return;
            }

            log("SSE 连接已关闭, messageId:", currentMessageId);

            if (isStreamingRef.current) {
              log("连接异常关闭");

              if (currentStreamingContent) {
                setError(new Error("连接异常关闭"));
              }

              setIsStreaming(false);
              setIsAIStreaming(false);
              setAiTypingEnd(true);
              onStreamEnd?.({ reason: "error", messageId: currentMessageId });

              setStreamingContent("");
            }

            sseClientRef.current = null;
          },
        });
        sseClientRef.current = client;
        setGlobalStopStreaming(() => stopStreamingRef.current());
        await client.connect();

        // 连接完成后，检查 chat 请求是否失败
        // 使用轮询检查，最多等待 5 秒
        const maxWaitTime = 5000;
        const checkInterval = 50;
        let elapsedTime = 0;

        while (elapsedTime < maxWaitTime) {
          if (chatRequestFailed && chatRequestError) {
            log("Chat 请求失败，抛出存储的错误");
            throw chatRequestError;
          }
          // 如果已经收到第一条消息，说明请求成功
          if (hasCalledSuccessCallback) {
            log("Chat 请求成功，继续执行");
            break;
          }
          await new Promise((resolve) => setTimeout(resolve, checkInterval));
          elapsedTime += checkInterval;
        }

        if (chatRequestFailed && chatRequestError) {
          throw chatRequestError;
        }
      } catch (err) {
        log("发送失败:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsStreaming(false);
        setIsAIStreaming(false);
        setStreamingContent("");
        throw err;
      }
    },
    [
      url,
      headers,
      timeout,
      autoReconnect,
      log,
      sessionId,
      addOutgoingMessage,
      setRobotMessage,
      updateRobotMessage,
      onStreamEnd,
      setAiTypingEnd,
      setGlobalStopStreaming,
      setIsAIStreaming,
    ],
  );

  /**
   * 发送chat请求
   */
  const sendChatRequest = useCallback(
    async (params: StartConnectionPropsType, requestId: string) => {
      // 准备请求数据
      const {
        bizType,
        content,
        contentType,
        contentId,
        parentContent,
        parentContentId,
        parentContentType,
      } = params;
      // 发送对应消息
      const chatParams = {
        input: content,
        requestId,
        history: [],
        chatbot: [],
        origin: isApp() ? OriginType.app : OriginType.web,
        machineModel: "",
        behaviorCollect: requireReportProps({
          bizType,
          content,
          contentType,
          contentId,
          parentContent,
          parentContentId,
          parentContentType,
          scene: ReportSceneTypeEnum.AI_CONSULT,
        }),
        uuid: sessionId, // 用于标识用户的唯一 ID
        sessionId, // 用来标记这次会话 ID
        cacheFlag: false,
      };

      // 发送消息到服务端
      await request.post(sendUrl, chatParams, {
        headers: {
          "x-robot-user": sessionId || "",
        },
      });
    },
    [sendUrl, sessionId, requireReportProps],
  );
  /**
   * 重试最后一条消息
   */
  const retry = useCallback(async () => {
    if (!lastRequestDataRef.current) {
      throw new Error("没有可重试的消息");
    }
    log("重试最后一条消息");

    // 重置错误状态和消息的错误标记
    setError(null);
    // 注意：这里的 streamingMessageIdRef.current 在 retry 时仍然指向最后一条消息的 ID
    updateRobotMessage(streamingMessageIdRef.current, {
      error: false,
      streaming: true,
      content: "",
    });

    await sendMessage(lastRequestDataRef.current, undefined, true);
  }, [sendMessage, log, updateRobotMessage]);

  /**
   * 清空消息
   */
  const clearMessages = useCallback(() => {
    log("清空消息历史");

    if (isStreamingRef.current) {
      stopStreamingRef.current();
    }

    setMessages([]);
    setStreamingContent("");
    setError(null);
    lastUserMessageRef.current = "";
    lastRequestDataRef.current = null;
  }, [log]);

  return {
    messages,
    streamingContent,
    isStreaming,
    error,
    sendMessage,
    stopStreaming,
    clearMessages,
    retry,
  };
}
